(() => {
    'use strict';

    /* ------------------------------------------------------------------
     * Block reveals.
     *
     * Every block already on screen fades in on load, one shortly after the
     * next; blocks further down fade in as they are scrolled to. Nothing waits
     * for a scroll that may never come — a visitor who lands and reads without
     * touching the wheel sees a complete page.
     *
     * The hidden state lives in CSS behind html.mb-reveal, which the small
     * script in <head> adds before first paint. This file's only job is to hand
     * out .is-revealed. If it never runs, that head script drops the class again
     * and every block simply renders — the hide can never outlive the code that
     * undoes it.
     *
     * Exposed as MB.reveal.init/destroy because swup swaps the page without a
     * reload: the blocks are new every navigation, and the listeners below have
     * to be taken down with the page that registered them or they accumulate.
     * ---------------------------------------------------------------- */

    const root = document.documentElement;

    // Gap between one block starting and the next. Matches $reveal-stagger.
    const STAGGER = 100;

    // How far up the viewport a block's top has to come before it fades in, as a fraction
    // of viewport height. 1 would fire on its very first pixel, which is what made blocks
    // appear the instant they were in sight; 0.8 waits until a fifth of a screen of the
    // block is showing.
    const TRIGGER = 0.8;

    // Live state for the page currently on screen. Cleared by destroy().
    let pending = new Set();
    let observer = null;
    let listening = false;
    let ticking = false;

    const inViewport = (block) => block.getBoundingClientRect().top < window.innerHeight;

    const reachedTrigger = (block) =>
        block.getBoundingClientRect().top < window.innerHeight * TRIGGER;

    // The escape hatch that keeps a stricter trigger safe. A block at the very end of the
    // page can run out of scroll before its top ever climbs to TRIGGER, and would then sit
    // there invisible — the exact way the first version of this stranded three blocks.
    const atPageEnd = () =>
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    /**
     * Reveal a set of blocks one after the other rather than all together.
     *
     * The delay is handed to CSS instead of being run off a chain of timers: the browser
     * schedules it against the same clock as the transition itself, so the cascade cannot
     * drift or land out of order on a busy frame.
     */
    const revealBatch = (list) => {
        // Observer entries arrive in whatever order they were noticed; a cascade only
        // reads as one if it follows the page.
        list.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

        list.forEach((block, index) => {
            if (index) {
                block.style.setProperty('--mb-reveal-delay', (index * STAGGER) + 'ms');
            }

            block.classList.add('is-revealed');
            pending.delete(block);
        });
    };

    const stopListening = () => {
        if (!listening) {
            return;
        }

        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        listening = false;
    };

    // The safety net, and the thing that actually guarantees the promise: any pending
    // block the viewport has reached gets revealed, full stop. An IntersectionObserver
    // alone cannot promise this — give it a negative bottom margin for a nicer trigger
    // point and the last block on the page can sit inside that dead band with no scroll
    // left to push it out, staying invisible forever. Measured: it stranded three.
    const sweep = () => {
        ticking = false;

        const ended = atPageEnd();
        const arrived = [];

        pending.forEach((block) => {
            if (ended || reachedTrigger(block)) {
                arrived.push(block);
            }
        });

        if (arrived.length) {
            revealBatch(arrived);
        }

        if (!pending.size) {
            stopListening();
        }
    };

    function onScroll() {
        if (ticking) {
            return;
        }

        ticking = true;
        window.requestAnimationFrame(sweep);
    }

    const blocksOnPage = () => Array.from(
        document.querySelectorAll('main.main > section, main.main > article > section')
    );

    /** Show everything, unconditionally. The last resort, never the normal path. */
    const revealAll = () => {
        blocksOnPage().forEach((block) => block.classList.add('is-revealed'));
        pending.clear();
    };

    const destroy = () => {
        stopListening();

        if (observer) {
            observer.disconnect();
            observer = null;
        }

        pending = new Set();
        ticking = false;
    };

    const init = () => {
        destroy();

        // Reduced motion, or the failsafe already fired: nothing is hidden, so there is
        // nothing to reveal.
        if (!root.classList.contains('mb-reveal')) {
            return;
        }

        // Tells the head script's failsafe to stand down: from here the hiding has an owner.
        root.setAttribute('data-reveal-ready', '');

        const blocks = blocksOnPage();

        if (!blocks.length) {
            root.classList.remove('mb-reveal');
            return;
        }

        pending = new Set(blocks);

        // Measure after layout has settled: web fonts and images shift where blocks are,
        // and measuring too early puts a block below the fold that is really on it.
        window.requestAnimationFrame(() => {
            // On load the blocks already on screen come in one after the other.
            revealBatch(blocks.filter(inViewport));

            if (!pending.size) {
                return;
            }

            if ('IntersectionObserver' in window) {
                observer = new IntersectionObserver((entries) => {
                    const arrived = [];

                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) {
                            return;
                        }

                        arrived.push(entry.target);
                        observer.unobserve(entry.target);
                    });

                    // Several blocks can cross the edge in one callback — short ones, or a
                    // jump down the page. They cascade rather than appearing as a slab.
                    if (arrived.length) {
                        revealBatch(arrived);
                    }
                    // Derived from TRIGGER rather than written out, so the observer and the
                    // scroll sweep can never drift to different trigger points.
                }, { threshold: 0, rootMargin: '0px 0px -' + Math.round((1 - TRIGGER) * 100) + '% 0px' });

                pending.forEach((block) => observer.observe(block));
            }

            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll);
            listening = true;
        });
    };

    window.MB = window.MB || {};
    window.MB.reveal = { init, destroy, revealAll };

    init();
})();
